import { captureEvent } from "@/lib/analytics-loader";
import { Button, Card, CardContent } from "@repo/ui";
import { Check, X } from "lucide-react";
import {
  useAcceptInvitation,
  useRejectInvitation,
  useSuspenseOrganizationList,
  useUserInvitationsQuery,
} from "../../lib/queries/organization";

interface PendingInvitationsListProps {
  onEmptyStateAction?: () => void;
}

export function PendingInvitationsList({
  onEmptyStateAction,
}: PendingInvitationsListProps) {
  const { data: invitationsResponse, refetch } =
    useUserInvitationsQuery("pending");
  const { data: organizations } = useSuspenseOrganizationList();
  const acceptInvitation = useAcceptInvitation();
  const rejectInvitation = useRejectInvitation();

  const invitations = invitationsResponse?.invitations || [];

  if (invitations.length === 0) {
    return null;
  }

  const handleAccept = (invitationId: string) => {
    acceptInvitation.mutate(invitationId, {
      onSuccess: () => {
        captureEvent("invitation_accepted", {
          invitationId,
        });
      },
    });
  };

  const handleDecline = (invitationId: string) => {
    rejectInvitation.mutate(invitationId, {
      onSuccess: async () => {
        const { data: updatedInvitations } = await refetch();
        const remainingInvitations = updatedInvitations?.invitations || [];

        // Check if this was the last invite AND user has no orgs
        if (
          remainingInvitations.length === 0 &&
          (!organizations || organizations.length === 0)
        ) {
          onEmptyStateAction?.();
        }
      },
    });
  };

  return (
    <div className="w-full max-w-md space-y-4">
      <div className="text-sm font-medium text-muted-foreground px-1">
        Pending Invitations
      </div>
      <Card>
        <CardContent className="grid gap-2 p-4">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              className="flex items-center justify-between p-3 rounded-lg border bg-muted/40"
            >
              <div className="flex flex-col overflow-hidden mr-3">
                <span className="font-medium truncate">
                  {/*
                    The invitation object from listUser might have different shapes depending on expansion.
                    We assume it has organization name or we look it up if joined.
                    Actually, the API response for listUser usually includes the organization object if expanded,
                    but the type might just be Invitation.
                    Let's see what properties are available on the Invitation type or if we need to cast/check.
                    Ideally, the creation includes organization name in the email, but here we need it for display.
                    The listUser procedure in backend calls ctx.auth.api.listInvitations.
                    BetterAuth invitations usually include organizationId.
                    We might need to fetch org details or rely on it being included.
                    Let's assume for now we might only have organizationId and iterate if names are missing.
                    Wait, `invitation.organization` might be structured if using Drizzle relations?
                    The router casts result to InvitationListResponse.
                    Let's try to display invitation.organization?.name if available, fallback to ID.
                   */}
                  {(
                    invitation as unknown as {
                      organization?: { name: string };
                    }
                  ).organization?.name || "Unknown Organization"}
                </span>
                <span className="text-xs text-muted-foreground capitalize">
                  {invitation.role} • Invited by{" "}
                  {(invitation as unknown as { inviter?: { name: string } })
                    .inviter?.name || "Member"}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/20"
                  onClick={() => handleDecline(invitation.id)}
                  disabled={rejectInvitation.isPending}
                  title="Decline"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  className="h-8 w-8 bg-green-600 hover:bg-green-700 text-white border-green-700"
                  onClick={() => handleAccept(invitation.id)}
                  disabled={acceptInvitation.isPending}
                  title="Accept"
                >
                  <Check className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
